import { Stack, Tooltip } from '@mui/material';
import { Button, colors, IconButton } from '@sweep-io/sweep-design';
import { Plus } from '@sweep-io/sweep-design/dist/icons';
import usePopover from '../../../hooks/usePopover';
import { forwardRef } from 'react';
import { MetadataSelectionPopover } from './MetadataSelectionPopover';
import { MetadataTypeProperties } from '@server/documentation';
import { UI_EVENTS } from '../../../services/events';
import useSendBiEvent from '../../../hooks/useSendBiEvent';

export const AddMetadata = forwardRef<
  HTMLDivElement,
  {
    onAddMetadata: (tabId: string, isPinned: boolean) => void;
    selectedTypes?: string[];
    compact?: boolean;
    crmOrgId: string;
  }
>(({ onAddMetadata, selectedTypes = [], compact = false, crmOrgId }, ref) => {
  const { isPopoverOpen, openPopover, closePopover, anchorElement, anchorRef } = usePopover();
  const sendBiEvent = useSendBiEvent();

  const handleSelectMetadata = (metadataType: MetadataTypeProperties) => {
    onAddMetadata(metadataType.name, metadataType.pinned);
    sendBiEvent({ name: UI_EVENTS.documentationAddMetadataBrowseTab });
  };

  return (
    <>
      <Stack
        data-testid="add-metadata-button"
        ref={(node) => {
          // Forward the ref
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          // Also set as anchor for popover
          anchorRef.current = node;
        }}
        sx={{
          ml: 0,
          flexShrink: 0,
          mr: 1,
          pl: 1,
          alignItems: 'center',
          height: '100%',
          justifyContent: 'center',

          '& button': {
            minWidth: '0',
          },
        }}
      >
        {/* by product definition only 10 tabs is allowed */}
        <Tooltip
          title={
            selectedTypes.length === 10
              ? 'Maximum tabs reached. Close a tab to open another.'
              : compact
                ? 'Add Metadata Type'
                : ''
          }
          placement="top"
          disableHoverListener={selectedTypes.length === 10 ? false : !compact}
        >
          <span>
            {compact ? (
              <Stack
                sx={{
                  //temporary until we move IconButton to new button types
                  '& button': {
                    '& svg path': {
                      stroke: selectedTypes.length === 10 ? undefined : colors.blue[500],
                    },
                  },
                }}
              >
                <IconButton
                  pressed={isPopoverOpen}
                  variant="flat"
                  disabled={selectedTypes.length === 10}
                  onClick={openPopover}
                  size="tiny"
                >
                  <Plus />
                </IconButton>
              </Stack>
            ) : (
              <Button
                pressed={isPopoverOpen}
                variant="flat-blue"
                disabled={selectedTypes.length === 10}
                onClick={openPopover}
                size={'tiny'}
              >
                + Add Metadata Type
              </Button>
            )}
          </span>
        </Tooltip>
      </Stack>

      <MetadataSelectionPopover
        isOpen={isPopoverOpen}
        onClose={closePopover}
        anchorEl={anchorElement}
        onSelectMetadata={handleSelectMetadata}
        crmOrgId={crmOrgId}
        selectedTypes={selectedTypes}
      />
    </>
  );
});
